import * as XLSX from 'xlsx';
import { formatInTimeZone } from 'date-fns-tz';
import { roundMoney, textoTarifas } from '../payroll';

export interface ClosureExportLine {
  dni: string;
  workerName: string;
  daysWorked: number;
  hoursWorked: number;
  amount: number;
  /** `[[tarifa, días]]` de más días a menos (`rate_breakdown`). */
  rates: [number, number][];
  /** Soles que agregó el feriado, ya dentro de `amount`. */
  feriado: number;
  notes: string[];
}

export interface ClosureExportMeta {
  campaignName: string;
  client: string;
  periodStart: string;
  periodEnd: string;
}

const TZ = 'America/Lima';
const DETAIL_HEADERS = [
  'DNI', 'Trabajador', 'Días', 'Horas', 'Tarifa(s) S/', 'Feriado S/', 'Monto S/', 'Observaciones',
];

/** Una tarifa sola va como NÚMERO, para que se pueda sumar en Excel; varias, como texto con sus días. */
function celdaTarifas(rates: [number, number][]): string | number {
  return rates.length === 1 ? rates[0][0] : textoTarifas(rates);
}

/** Texto apto para nombre de archivo: sin tildes, minúsculas y separado por guiones. */
export function slugify(s: string): string {
  const out = s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // marcas diacríticas que dejó NFD (tildes, virgulilla de la ñ)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return out === '' ? 'sin-nombre' : out;
}

export function closureFileName(meta: ClosureExportMeta): string {
  return `cierre-${slugify(meta.campaignName)}-${meta.periodStart}-a-${meta.periodEnd}.xlsx`;
}

/** Libro del cierre: hoja Resumen (cabecera + total) y hoja Detalle (una fila por trabajador). */
export function buildClosureWorkbook(
  meta: ClosureExportMeta,
  lines: ClosureExportLine[],
  generatedAt: Date = new Date(),
): ArrayBuffer {
  const total = roundMoney(lines.reduce((sum, l) => sum + l.amount, 0));

  const resumen = XLSX.utils.aoa_to_sheet([
    ['Campaña', meta.campaignName],
    ['Cliente', meta.client],
    ['Periodo', `${meta.periodStart} a ${meta.periodEnd}`],
    ['Total S/', total],
    ['Generado el', formatInTimeZone(generatedAt, TZ, 'dd/MM/yyyy HH:mm')],
  ]);
  resumen['!cols'] = [{ wch: 14 }, { wch: 42 }];

  const detalle = XLSX.utils.aoa_to_sheet([
    DETAIL_HEADERS,
    ...lines.map((l) => [
      l.dni, l.workerName, l.daysWorked, l.hoursWorked, celdaTarifas(l.rates), l.feriado, l.amount,
      l.notes.join('; '),
    ]),
  ]);
  // El DNI se fuerza a texto: si Excel lo toma como número pierde ceros a la izquierda.
  lines.forEach((l, i) => {
    detalle[`A${i + 2}`] = { t: 's', v: l.dni };
  });
  detalle['!cols'] = [
    { wch: 12 }, { wch: 32 }, { wch: 8 }, { wch: 8 }, { wch: 20 }, { wch: 11 }, { wch: 12 }, { wch: 60 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, resumen, 'Resumen');
  XLSX.utils.book_append_sheet(wb, detalle, 'Detalle');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

/* ---------------------------------------------------------------------------
 * Planilla de pago — el formato con el que la operación paga de verdad
 *
 * Copia de «Pagos MAKRO Impulso - 2026 ejemplo.xlsx» que el usuario entregó el 2-sep: sus 16
 * columnas, en ese orden, una fila por persona, más «Pago feriado» al lado del bono (usuario, 3-nov:
 * el recargo de feriado ya va dentro del total y tiene que verse aparte). Ojo: corre un lugar a la
 * derecha todo lo que viene después del bono. Es un archivo que se pega sobre la plantilla
 * de finanzas, así que las columnas que el sistema no conoce —orden de compra, estado del correo,
 * tareo de ruta— van PRESENTES Y VACÍAS: quitarlas obligaría a reacomodar a mano cada mes.
 *
 * Decisiones del usuario del 2026-09-02:
 *   · «Monto diario» = la tarifa que gobierna MÁS días («si tuvo más días de 60 sale 60»). Si
 *     empatan, la celda muestra las dos: «60 / 70». El promedio (S/62,07) se descartó por no ser
 *     la tarifa de nadie, y elegir una al azar en el empate hubiera sido peor: irreproducible.
 *   · Bloques por supervisor, con su encabezado y su cabecera de columnas repetida, tal como el
 *     archivo del cliente. El supervisor sale de la TIENDA (0079): el usuario repartió las 19
 *     Makro entre Miguel (10) y Alanish (9).
 * ------------------------------------------------------------------------- */

export interface PaymentSheetLine {
  documentNumber: string;
  documentType: string;
  workerName: string;
  bank: string;
  accountNumber: string;
  cci: string;
  daysWorked: number;
  /**
   * Tarifa mayoritaria del periodo, o `null` si empatan (o si se pagó por hora).
   *
   * Con `null` y `dailyRates` de dos o más, la celda sale como «60 / 70». Con `null` y ninguna
   * tarifa por jornada, sale vacía: «monto diario» no aplica.
   */
  dailyRate: number | null;
  /** Todas las tarifas base del periodo, de más días a menos. Solo se usa para resolver el empate. */
  dailyRates: number[];
  /** Bono de asistencia. 0 se escribe como celda VACÍA, igual que en el archivo del cliente. */
  bono: number;
  /** Lo que agregó el feriado, ya dentro de `total`. 0 va como celda vacía, igual que el bono. */
  feriado: number;
  total: number;
  notes: string[];
  /** Nombre del supervisor de campo; vacío ⇒ va al bloque «Sin supervisor asignado». */
  supervisor: string;
}

export interface PaymentSheetMeta extends ClosureExportMeta {
  /** `campaigns.analytic_account` (ej. S000169736). Vacío si la campaña no lo tiene cargado. */
  analyticAccount: string;
}

const PAYMENT_HEADERS = [
  'N° Documento', 'Tipo documento', 'Apellidos y Nombres', 'Banco', 'N° cuenta', 'N° CCI',
  'Dias trabajados', 'Monto diario', 'Bono de 100 soles', 'Pago feriado', 'Total a pagar', 'Campaña',
  'Cuenta Analítica', 'N° orden de compra', 'Estado correo', 'Observación', 'Tareo Ruta',
];

export function paymentSheetFileName(meta: PaymentSheetMeta): string {
  return `planilla-${slugify(meta.campaignName)}-${meta.periodStart}-a-${meta.periodEnd}.xlsx`;
}

/**
 * La celda «Monto diario»: un número cuando hay tarifa mayoritaria, «60 / 70» cuando empatan y
 * vacío cuando no hubo ninguna tarifa por jornada. Nunca un promedio.
 */
function celdaMontoDiario(l: PaymentSheetLine): string | number {
  if (l.dailyRate !== null) return l.dailyRate;
  return l.dailyRates.length > 1 ? l.dailyRates.join(' / ') : '';
}

/**
 * Ordena las líneas en bloques por supervisor, como el archivo del cliente.
 *
 * Los supervisores van alfabéticos y «Sin supervisor asignado» siempre al final: si aparece, es un
 * reparto incompleto y conviene que salte a la vista abajo de todo, no escondido entre dos bloques.
 */
function groupBySupervisor(lines: PaymentSheetLine[]): [string, PaymentSheetLine[]][] {
  const SIN = 'Sin supervisor asignado';
  const groups = new Map<string, PaymentSheetLine[]>();
  for (const l of lines) {
    const key = l.supervisor.trim() === '' ? SIN : l.supervisor.trim();
    const bucket = groups.get(key);
    if (bucket) bucket.push(l);
    else groups.set(key, [l]);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === SIN) return 1;
    if (b === SIN) return -1;
    return a.localeCompare(b, 'es');
  });
}

/** Libro de la planilla de pago: una hoja, bloques por supervisor, 17 columnas por bloque. */
export function buildPaymentSheetWorkbook(
  meta: PaymentSheetMeta,
  lines: PaymentSheetLine[],
): ArrayBuffer {
  const rows: (string | number)[][] = [];
  /** Filas (1-indexadas) que llevan documento, cuenta y CCI: hay que forzarlas a texto después. */
  const dataRows: { row: number; line: PaymentSheetLine }[] = [];

  for (const [supervisor, block] of groupBySupervisor(lines)) {
    if (rows.length > 0) rows.push([]); // línea en blanco entre bloques, como el archivo origen
    rows.push([`Supervisor - ${supervisor.toUpperCase()}`]);
    rows.push([...PAYMENT_HEADERS]);
    for (const l of block) {
      dataRows.push({ row: rows.length + 1, line: l });
      rows.push([
        l.documentNumber,
        l.documentType,
        l.workerName,
        l.bank,
        l.accountNumber,
        l.cci,
        l.daysWorked,
        celdaMontoDiario(l),
        l.bono > 0 ? l.bono : '',
        l.feriado > 0 ? l.feriado : '',
        l.total,
        meta.campaignName,
        meta.analyticAccount,
        '', // N° orden de compra — la asigna finanzas, fuera del sistema
        '', // Estado correo
        l.notes.join('; '),
        '', // Tareo Ruta
      ]);
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Documento, cuenta y CCI van forzados a TEXTO. El CCI de 20 dígitos empieza en 0 casi siempre
  // ("00219117955744004851"): si Excel lo toma como número pierde el cero y la transferencia se
  // rechaza. Es el error más caro que puede tener este archivo.
  for (const { row, line } of dataRows) {
    ws[`A${row}`] = { t: 's', v: line.documentNumber };
    ws[`E${row}`] = { t: 's', v: line.accountNumber };
    ws[`F${row}`] = { t: 's', v: line.cci };
  }

  ws['!cols'] = [
    { wch: 13 }, { wch: 14 }, { wch: 34 }, { wch: 26 }, { wch: 22 }, { wch: 24 },
    { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 13 }, { wch: 40 },
    { wch: 15 }, { wch: 18 }, { wch: 13 }, { wch: 60 }, { wch: 12 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Planilla');
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}
